import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const HOURS = [
  { day: "Lundi", time: "Fermé" },
  { day: "Mardi — Vendredi", time: "14:00 — 23:30" },
  { day: "Samedi", time: "12:00 — 00:00" },
  { day: "Dimanche", time: "15:00 — 23:00" },
];

function isOpenNow() {
  const now = new Date();
  const d = now.getDay();
  const h = now.getHours() + now.getMinutes() / 60;
  if (d === 1) return false;
  if (d >= 2 && d <= 5) return h >= 14 && h < 23.5;
  if (d === 6) return h >= 12;
  return h >= 15 && h < 23;
}

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-cyan-400";

export const LocationContact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const open = isOpenNow();

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé, on vous répond vite !");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Impossible d'envoyer le message"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="infos" data-testid="location-section" className="relative py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-hud text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 05 — Accès & contact"}
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Rejoignez <span className="text-glow-cyan text-cyan-400">l'arène</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <div className="clip-frame relative h-48 overflow-hidden border border-line">
              <img
                src="/photos/entree.jpg"
                alt="Entrée de la salle Immersion Plus"
                loading="lazy"
                className="h-full w-full object-cover object-[50%_22%]"
              />
            </div>
            <div className="clip-frame relative h-64 overflow-hidden border border-line">
              <iframe
                data-testid="location-map"
                title="Plan Immersion Plus"
                src="https://www.openstreetmap.org/export/embed.html?bbox=2.848%2C48.912%2C2.902%2C48.938&layer=mapnik&marker=48.9257%2C2.8750"
                className="map-dark h-full w-full"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="border border-line bg-surface p-5">
                <div className="flex items-center gap-2 text-cyan-400">
                  <MapPin className="h-4 w-4" />
                  <span className="font-hud text-[10px] uppercase tracking-[0.25em]">Adresse</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  40 avenue de la Grande Haie
                  <br />
                  77100 Mareuil-lès-Meaux
                  <br />
                  <span className="text-slate-500">À 5 min de la gare de Meaux</span>
                </p>
              </div>
              <div className="border border-line bg-surface p-5">
                <div className="flex items-center gap-2 text-cyan-400">
                  <Phone className="h-4 w-4" />
                  <span className="font-hud text-[10px] uppercase tracking-[0.25em]">Contact</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  Fixe : 09 88 45 18 67
                  <br />
                  Portable : 06 68 91 53 69
                  <br />
                  contact@immersionplus.fr
                </p>
              </div>
            </div>
            <div data-testid="opening-hours" className="border border-line bg-surface p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-cyan-400">
                  <Clock className="h-4 w-4" />
                  <span className="font-hud text-[10px] uppercase tracking-[0.25em]">Horaires</span>
                </div>
                <span
                  data-testid="open-status"
                  className={`flex items-center gap-2 px-3 py-1 font-hud text-[10px] font-bold uppercase tracking-[0.2em] ${
                    open
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-red-500/15 text-red-400"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${open ? "bg-emerald-400" : "bg-red-400"}`} />
                  {open ? "Ouvert" : "Fermé"}
                </span>
              </div>
              <div className="mt-4 space-y-2">
                {HOURS.map((h) => (
                  <div key={h.day} className="flex justify-between border-b border-line/60 pb-2 text-sm last:border-0">
                    <span className="text-slate-400">{h.day}</span>
                    <span className="font-hud text-xs text-slate-200">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="glass-panel flex flex-col gap-4 p-6 sm:p-8"
          >
            <h3 className="font-display text-xl font-bold uppercase text-slate-100">
              Une question ? Un événement ?
            </h3>
            <p className="text-sm text-slate-400">
              Anniversaire, team-building, privatisation : écrivez-nous, on
              s'occupe du reste.
            </p>
            <input
              data-testid="contact-name-input"
              placeholder="Votre nom"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              type="email"
              placeholder="Votre email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className={inputCls}
            />
            <textarea
              data-testid="contact-message-input"
              placeholder="Votre message"
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="mt-2 flex items-center justify-center gap-2 bg-cyan-400 py-3.5 font-hud text-xs font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.45)] disabled:opacity-40"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer le message
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
